import { Editor, Element as SlateElement, Transforms } from "slate";

import type {
  CustomEditor,
  CustomTextKey,
  CustomElementFormat,
  AlignType,
  ListType,
  AccordionElement,
  AccordionItemElement,
} from "./types";

const LIST_TYPES = ["numbered-list", "bulleted-list"] as const;
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"] as const;

// Define our custom set of helpers
export const CustomCommands = {
  isBlockActive(
    editor: CustomEditor,
    format: CustomElementFormat,
    blockType: "type" | "align" = "type",
  ) {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => {
          if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
            if (blockType === "align" && hasAlign(n)) {
              return n.align === format;
            }
            return n.type === format;
          }
          return false;
        },
      }),
    );

    return !!match;
  },

  isMarkActive(editor: CustomEditor, format: CustomTextKey) {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  },

  isIndentable(editor: CustomEditor) {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) =>
          !Editor.isEditor(n)
          && SlateElement.isElement(n)
          && Editor.isBlock(editor, n),
      }),
    );

    return !!match;
  },

  getCurrentIndent(editor: CustomEditor) {
    const { selection } = editor;
    if (!selection) return 0;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) =>
          !Editor.isEditor(n)
          && SlateElement.isElement(n)
          && Editor.isBlock(editor, n),
      }),
    );

    if (match) {
      const [node] = match;
      if (SlateElement.isElement(node) && hasIndent(node)) {
        return node.indent || 0;
      }
    }

    return 0;
  },

  toggleBlock(editor: CustomEditor, format: CustomElementFormat) {
    const { selection } = editor;
    if (!selection) return;

    const isActive = CustomCommands.isBlockActive(
      editor,
      format,
      isAlignType(format) ? "align" : "type",
    );
    const isList = isListType(format);

    if (isList || (!isAlignType(format) && !isActive)) {
      Transforms.unwrapNodes(editor, {
        at: selection,
        match: (n) =>
          !Editor.isEditor(n)
          && SlateElement.isElement(n)
          && isListType(n.type),
        split: true,
      });
    }

    let newProperties: Partial<SlateElement>;
    if (isAlignType(format)) {
      newProperties = {
        align: isActive ? undefined : format,
      };
    } else {
      newProperties = {
        type: isActive ? "paragraph" : isList ? "list-item" : format,
      };
    }

    Transforms.setNodes<SlateElement>(editor, newProperties, {
      at: selection,
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n),
    });

    if (!isActive && isList) {
      Transforms.wrapNodes(
        editor,
        { type: format, children: [] },
        {
          at: selection,
        },
      );
    }
  },

  toggleMark(editor: CustomEditor, format: CustomTextKey) {
    const isActive = CustomCommands.isMarkActive(editor, format);

    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  },

  increaseIndent(editor: CustomEditor) {
    const blocks = getSelectedBlocks(editor);

    for (const [node, path] of blocks) {
      if (SlateElement.isElement(node) && hasIndent(node)) {
        const currentIndent = node.indent || 0;
        Transforms.setNodes(
          editor,
          { indent: Math.min(currentIndent + 1, 8) },
          { at: path },
        );
      }
    }
  },

  decreaseIndent(editor: CustomEditor) {
    const blocks = getSelectedBlocks(editor);

    for (const [node, path] of blocks) {
      if (SlateElement.isElement(node) && hasIndent(node)) {
        const currentIndent = node.indent || 0;
        const newIndent = currentIndent > 0 ? currentIndent - 1 : 0;

        if (newIndent === 0) {
          Transforms.unsetNodes(editor, "indent", { at: path });
        } else {
          Transforms.setNodes(editor, { indent: newIndent }, { at: path });
        }
      }
    }
  },

  insertAccordion(editor: CustomEditor) {
    const accordion: AccordionElement = {
      type: "accordion",
      children: [
        {
          type: "accordion-item",
          isOpen: true,
          children: [
            {
              type: "accordion-item-header",
              children: [{ text: "Item Title" }],
            },
            {
              type: "accordion-item-body",
              children: [
                {
                  type: "paragraph",
                  children: [{ text: "Item content..." }],
                },
              ],
            },
          ],
        },
      ],
    };

    Transforms.insertNodes(editor, accordion);
  },

  insertAccordionItem(editor: CustomEditor) {
    const { selection } = editor;
    if (!selection) return;

    // Find the accordion container (not just if we're inside one)
    const [accordionMatch] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) =>
          !Editor.isEditor(n)
          && SlateElement.isElement(n)
          && n.type === "accordion",
      }),
    );

    if (accordionMatch) {
      const [, accordionPath] = accordionMatch;

      const item: AccordionItemElement = {
        type: "accordion-item",
        isOpen: true,
        children: [
          {
            type: "accordion-item-header",
            children: [{ text: "New Item" }],
          },
          {
            type: "accordion-item-body",
            children: [
              {
                type: "paragraph",
                children: [{ text: "Item content..." }],
              },
            ],
          },
        ],
      };

      // Insert at the end of the accordion's children (as sibling)
      const accordionChildrenLength = (accordionMatch[0] as any).children
        .length;
      Transforms.insertNodes(editor, item, {
        at: [...accordionPath, accordionChildrenLength],
      });
    }
  },

  toggleAccordionItem(editor: CustomEditor, element: AccordionItemElement) {
    const { selection } = editor;
    if (!selection) return;

    const [match] = Array.from(
      Editor.nodes(editor, {
        match: (n) => n === element,
      }),
    );

    if (match) {
      const [, path] = match;
      Transforms.setNodes(editor, { isOpen: !element.isOpen }, { at: path });
    }
  },

  // Smart command that adds accordion or item based on context
  insertAccordionOrItem(editor: CustomEditor) {
    const { selection } = editor;
    if (!selection) return;

    // Check if we're inside an accordion
    const [accordionMatch] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) =>
          !Editor.isEditor(n)
          && SlateElement.isElement(n)
          && n.type === "accordion",
      }),
    );

    if (accordionMatch) {
      // Inside accordion - add new item
      CustomCommands.insertAccordionItem(editor);
    } else {
      // Not inside accordion - add new accordion
      CustomCommands.insertAccordion(editor);
    }
  },
};

// Helper functions
const isAlignType = (format: CustomElementFormat): format is AlignType => {
  return TEXT_ALIGN_TYPES.includes(format as AlignType);
};

const isListType = (format: CustomElementFormat): format is ListType => {
  return LIST_TYPES.includes(format as ListType);
};

const hasAlign = (
  element: SlateElement,
): element is SlateElement & { align?: string } => {
  return "align" in element;
};

const hasIndent = (
  element: SlateElement,
): element is SlateElement & { indent?: number } => {
  const nonIndentableTypes = [
    "accordion",
    "accordion-item",
    "accordion-item-header",
    "accordion-item-body",
  ];
  return !nonIndentableTypes.includes(element.type);
};

const getSelectedBlocks = (editor: CustomEditor) => {
  const { selection } = editor;
  if (!selection) return [];

  return Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) =>
        !Editor.isEditor(n)
        && SlateElement.isElement(n)
        && (n.type === "list-item" || Editor.isBlock(editor, n)),
      mode: "lowest", // Only get the lowest matching nodes
    }),
  );
};
